import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './styles.css';
import type { PhotoCollageApi, ProjectBundle, ProjectLoadResponse } from '../shared/ipc';
import { decodeProjectArchive, encodeProjectArchive } from '../shared/projectArchive';

document.title = `PhotoCollage © BArko, 2026 — v${__APP_VERSION__}`;

function makeBrowserApi(): PhotoCollageApi {
  const recoveryKey = 'photocollage-web-recovery';
  const bytesFromJson = (value: unknown): Uint8Array => value instanceof Uint8Array ? value : Uint8Array.from(Array.isArray(value) ? value : Object.values(value as Record<string, number>));
  const decodeBundle = (value: any): ProjectBundle => ({
    document: value.document,
    assets: Object.fromEntries(Object.entries(value.assets ?? {}).map(([id, bytes]) => [id, bytesFromJson(bytes)])),
    fonts: Object.fromEntries(Object.entries(value.fonts ?? {}).map(([id, bytes]) => [id, bytesFromJson(bytes)])),
  });
  const readBundle = (raw: string, source: 'recovery' | 'saved'): ProjectLoadResponse => {
    try { return { status: 'ok', bundle: decodeBundle(JSON.parse(raw)), source }; }
    catch (error) { return { status: 'error', message: error instanceof Error ? error.message : 'Datoteke projekta ni mogoče prebrati.' }; }
  };
  const chooseFiles = (accept: string, multiple: boolean): Promise<File[]> => new Promise((resolve) => {
    const input = window.document.createElement('input');
    input.type = 'file'; input.accept = accept; input.multiple = multiple;
    input.onchange = () => resolve(Array.from(input.files ?? [])); input.click();
  });
  const saveDownload = (name: string, blob: Blob): void => {
    const url = URL.createObjectURL(blob); const anchor = window.document.createElement('a');
    anchor.href = url; anchor.download = name; anchor.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return {
    searchPixabay: async () => ({ status: 'error', message: 'Pixabay je na voljo samo v namizni aplikaciji.' }),
    getRandomPhotos: async () => ({ status: 'error', message: 'Zunanje fotografije so na voljo samo v namizni aplikaciji.' }),
    getAiKeyStatus: async () => ({ status: 'error', message: 'Varna sistemska shramba API ključev je na voljo samo v namizni aplikaciji.' }),
    saveAiApiKey: async () => ({ status: 'error', message: 'API ključev ni mogoče varno shraniti v tej različici aplikacije.' }),
    sendAiPrompt: async () => ({ status: 'error', message: 'Ta funkcija je na voljo samo v namizni aplikaciji.' }),
    downloadPixabayImage: async () => ({ status: 'error', message: 'Zunanje fotografije so na voljo samo v namizni aplikaciji.' }),
    importImages: async () => {
      const files = await chooseFiles('image/jpeg,image/png,image/webp,image/svg+xml', true);
      if (!files.length) return { status: 'cancelled' };
      const accepted = files.filter((file) => ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'].includes(file.type));
      return { status: 'ok', files: await Promise.all(accepted.map(async (file) => ({ name: file.name, mime: file.type as 'image/jpeg' | 'image/png' | 'image/webp' | 'image/svg+xml', bytes: new Uint8Array(await file.arrayBuffer()) }))), errors: files.length === accepted.length ? [] : ['Nekatere datoteke niso podprte.'] };
    },
    importFont: async () => {
      const [file] = await chooseFiles('.ttf,.otf,.woff,.woff2', false);
      return file ? { status: 'ok', file: { name: file.name, bytes: new Uint8Array(await file.arrayBuffer()) } } : { status: 'cancelled' };
    },
    listSystemFonts: async () => [],
    exportImage: async (bytes, format) => {
      saveDownload(`photocollage.${format === 'jpeg' ? 'jpg' : 'png'}`, new Blob([bytes], { type: format === 'jpeg' ? 'image/jpeg' : 'image/png' }));
      return { status: 'saved' };
    },
    setWindowDocumentName: async (name) => { document.title = `PhotoCollage © BArko, 2026 — v${__APP_VERSION__} - ${name || 'untitled'}`; },
    openExternal: async (url) => { window.open(url, '_blank', 'noopener,noreferrer'); },
    getLanguagePreference: async () => {
      try { const saved = localStorage.getItem('photocollage-language'); return saved === 'sl' || saved === 'en' ? saved : null; }
      catch { return null; }
    },
    setLanguagePreference: async (language) => { try { localStorage.setItem('photocollage-language', language); } catch { /* Use the in-memory choice for this session. */ } },
    loadStartupProject: async () => {
      try { const recovery = localStorage.getItem(recoveryKey); if (recovery) return readBundle(recovery, 'recovery'); }
      catch { return { status: 'empty', warning: 'Shramba brskalnika ni dosegljiva.' }; }
      return { status: 'empty' };
    },
    openProject: async () => {
      const [file] = await chooseFiles('.photocollage,application/zip', false);
      if (!file) return { status: 'cancelled' };
      try { return { status: 'ok', bundle: decodeProjectArchive(new Uint8Array(await file.arrayBuffer())), source: 'saved' }; }
      catch (error) { return { status: 'error', message: error instanceof Error ? error.message : 'Datoteke projekta ni mogoče odpreti.' }; }
    },
    saveProject: async (bundle) => {
      const archive = encodeProjectArchive(bundle);
      saveDownload(`${bundle.document.name || 'photocollage'}.photocollage`, new Blob([archive], { type: 'application/zip' }));
      return { status: 'saved', document: bundle.document };
    },
    writeRecovery: async (bundle) => { try { localStorage.setItem(recoveryKey, JSON.stringify(bundle)); return { status: 'ok' }; } catch (error) { return { status: 'error', message: error instanceof Error ? error.message : 'Osnutka ni mogoče shraniti.' }; } },
    clearRecovery: async () => { try { localStorage.removeItem(recoveryKey); return { status: 'ok' }; } catch (error) { return { status: 'error', message: error instanceof Error ? error.message : 'Osnutka ni mogoče izbrisati.' }; } },
    discardProject: async () => { return { status: 'ok' }; },
  };
}

if (!window.photoCollage) window.photoCollage = makeBrowserApi();

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
